import { useInView } from '../../hooks/useInView';
import { LayoutDashboard, Wallet, Target, TrendingUp, MessageSquare, Kanban } from 'lucide-react';

const features = [
  {
    icon: LayoutDashboard,
    title: 'Dashboard Financeiro Completo',
    description: 'Visualize receitas, despesas e saldo em tempo real. Gráficos intuitivos de evolução mensal, distribuição por categoria e projeção de 12 meses para planejar seu futuro financeiro.',
  },
  {
    icon: Wallet,
    title: 'Controle Total de Receitas e Despesas',
    description: 'Registre transações recorrentes e avulsas com categorias personalizadas. Edite, filtre por mês e acompanhe cada centavo que entra e sai da sua conta.',
  },
  {
    icon: Target,
    title: 'Metas Financeiras Inteligentes',
    description: 'Defina metas como viagem, reserva de emergência ou compra de imóvel. Acompanhe o progresso com barras visuais e escolha a fonte dos aportes — saldo existente ou renda externa.',
  },
  {
    icon: TrendingUp,
    title: 'Investimentos e Projeções com IA',
    description: 'Gerencie seu portfólio de investimentos, acompanhe cotações e receba projeções de saldo inteligentes que consideram receitas recorrentes, despesas fixas e gastos avulsos.',
  },
  {
    icon: MessageSquare,
    title: 'Bot WhatsApp com Inteligência Artificial',
    description: 'Registre gastos, consulte saldo, crie metas e receba relatórios direto pelo WhatsApp. Envie áudios e fotos de recibos — a IA transcreve e analisa automaticamente.',
  },
  {
    icon: Kanban,
    title: 'Quadro Kanban de Tarefas Financeiras',
    description: 'Organize compromissos financeiros com quadro Kanban, calendário mensal e lista. Arraste tarefas entre colunas, vincule a transações e receba resumos diários por WhatsApp.',
  },
];

export default function FeaturesSection() {
  const [ref, isInView] = useInView({ threshold: 0.15 });

  return (
    <section id="funcionalidades" className="relative py-32 overflow-hidden" data-testid="features-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,hsl(var(--primary)/0.06),transparent)]" />

      <div className="relative mx-auto max-w-7xl px-6" ref={ref}>
        <div className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-xs font-semibold uppercase tracking-widest text-primary mb-4 block">
            Funcionalidades
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-6">
            Plataforma completa de gestão financeira pessoal
          </h2>
          <p className="text-muted-foreground text-lg">
            Ferramentas inteligentes para controle de gastos, metas, investimentos e tarefas — com IA e WhatsApp integrados.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                data-testid={`feature-card-${index}`}
                className={`group relative transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="relative h-full rounded-2xl border border-border/60 bg-card/60 backdrop-blur-sm p-7 hover:bg-card/90 hover:border-primary/30 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_0_40px_-15px_hsl(var(--primary)/0.25)]">
                  <div className="mb-5 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 text-primary group-hover:scale-110 group-hover:bg-primary/20 transition-all duration-500">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-3 tracking-tight">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
